import { z } from "zod";
import {
  INTERVAL_MINUTES,
  INTERVAL_STEP_ISSUE,
  MAX_CRON_LENGTH,
  MAX_RUN_DATES,
  MAX_TIMEZONE_LENGTH,
  QUARTER_HOUR_ISSUE,
  WHOLE_HOUR_ISSUE,
  duplicateTime,
  intervalWindow,
  isQuarterHourMinute,
  isRealDate,
  isValidTimezone,
  offQuarterMinutes,
  parseTime12h,
  yearDateIssue,
  type FriendlySchedule,
} from "../cron/cronUtils";

/**
 * Schedule request and response contracts (FR-50, API-STANDARD.md §5).
 *
 * A schedule is written either with a raw `cron` expression or with the `friendly` builder,
 * never both: two sources for one column is how a UI and an API drift apart. The server
 * collapses `friendly` to the one expression the `cron` column holds.
 */

export const SCHEDULE_KINDS = ["cron", "event_drain"] as const;
export type ScheduleKind = (typeof SCHEDULE_KINDS)[number];

/** `HH:MMAM` / `HH:MMPM`, the form Studio's scheduler document already uses. */
const TIME_12H = /^(0[1-9]|1[0-2]):[0-5][0-9](AM|PM)$/i;

/** A whole hour in the same form, `10:00AM`: the interval window's edges. */
const WHOLE_HOUR_12H = /^(0[1-9]|1[0-2]):00(AM|PM)$/i;

/** Wave 3 (K2): a Monthly schedule names at least one day of the month. */
export const MONTHLY_NO_DAYS_ISSUE = "Choose at least one day of the month.";

const weekDaysSchema = z.array(z.int().min(0).max(6)).max(7).default([]);

/**
 * One or more times of day (Daily, Weekly, Monthly, Yearly). The same time twice is refused on the
 * SECOND occurrence (`cronSchedules.{i}`), with the time in words (2026-09-28, owner: "I was
 * able to save the 2.30 AM time twice without any validation"); it used to be merged silently.
 *
 * Yearly (round 10, C1) needs `yearDate`, a day every year has: April 31 is refused on
 * `yearDate.day`, and so is February 29 (`FEBRUARY_29_ISSUE`), which runs one year in four.
 */
const timesScheduleSchema = z
  .object({
    frequency: z.enum(["Daily", "Weekly", "Monthly", "Yearly"]),
    cronSchedules: z
      .array(z.string().regex(TIME_12H, "Use a time such as 01:00AM."))
      .min(1)
      .max(24)
      .superRefine((slots, ctx) => {
        const duplicate = duplicateTime(slots);
        if (duplicate)
          ctx.addIssue({ code: "custom", path: [duplicate.index], message: duplicate.issue });
      }),
    weekDays: weekDaysSchema,
    monthDays: z.array(z.int().min(1).max(31)).max(31).default([]),
    yearDate: z.object({ month: z.int().min(1).max(12), day: z.int().min(1).max(31) }).optional(),
  })
  .superRefine((value, ctx) => {
    // Wave 3 (K2): an empty Monthly day list used to degrade to "every day" (`friendlyToCron`);
    // the editor blocks it, and the server refuses it too.
    if (value.frequency === "Monthly" && value.monthDays.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["monthDays"],
        message: MONTHLY_NO_DAYS_ISSUE,
      });
      return;
    }
    if (value.frequency !== "Yearly") return;
    if (!value.yearDate) {
      ctx.addIssue({
        code: "custom",
        path: ["yearDate"],
        message: "Choose the month and day this schedule runs.",
      });
      return;
    }
    const issue = yearDateIssue(value.yearDate);
    if (issue)
      ctx.addIssue({ code: "custom", path: ["yearDate", issue.field], message: issue.issue });
  });

/**
 * Every 15, 30 or 60 minutes between two whole hours, on chosen weekdays (2026-09-28): see
 * `FriendlyIntervalSchedule`. The window includes its start and ends before its end; a window
 * that does not end after it starts is refused on `windowEnd`.
 */
const intervalScheduleSchema = z
  .object({
    frequency: z.literal("Interval"),
    intervalMinutes: z.literal(INTERVAL_MINUTES, { error: INTERVAL_STEP_ISSUE }),
    windowStart: z.string().regex(WHOLE_HOUR_12H, WHOLE_HOUR_ISSUE),
    windowEnd: z.string().regex(WHOLE_HOUR_12H, WHOLE_HOUR_ISSUE),
    weekDays: weekDaysSchema,
  })
  .superRefine((value, ctx) => {
    // An edge off the hour is already reported on its own field; checking the order as well
    // would report it a second time, on windowEnd.
    if (!WHOLE_HOUR_12H.test(value.windowStart) || !WHOLE_HOUR_12H.test(value.windowEnd)) return;
    try {
      intervalWindow(value);
    } catch (error) {
      ctx.addIssue({
        code: "custom",
        path: ["windowEnd"],
        message: error instanceof Error ? error.message : String(error),
      });
    }
  });

/**
 * Once (round 10 wave 2, S3, design §1.5): runs on chosen dates, each at its own time, local to
 * the schedule's zone (`date` `YYYY-MM-DD`, `time` `05:00PM`): 1 to `MAX_RUN_DATES`, each on the
 * quarter hour, no date and time twice. The server turns each into an instant
 * (`localDateTimeToInstant`) and refuses one a daylight-saving change skips, a new one already
 * past and one more than 400 days ahead (`ScheduleService`); the instants are `run_dates`.
 */
const datesScheduleSchema = z.object({
  frequency: z.literal("Dates"),
  runDates: z
    .array(
      z.object({
        date: z.string().refine(isRealDate, "Use a real date such as 2026-12-25."),
        time: z
          .string()
          .regex(TIME_12H, "Use a time such as 05:00PM.")
          .refine((time) => isQuarterHourMinute(parseTime12h(time).minute), QUARTER_HOUR_ISSUE),
      }),
    )
    .min(1, "Add at least one date and time.")
    .max(MAX_RUN_DATES, `A schedule can run on at most ${MAX_RUN_DATES} dates.`)
    .superRefine((rows, ctx) => {
      const seen = new Set<string>();
      rows.forEach((row, index) => {
        const key = `${row.date} ${row.time.toUpperCase()}`;
        if (seen.has(key)) {
          ctx.addIssue({
            code: "custom",
            path: [index, "time"],
            message: "This date and time is already in this schedule.",
          });
        }
        seen.add(key);
      });
    }),
});

export const friendlyScheduleSchema = z.discriminatedUnion("frequency", [
  timesScheduleSchema,
  intervalScheduleSchema,
]);
export type FriendlyScheduleBody = z.infer<typeof friendlyScheduleSchema>;

/**
 * Once (round 10, C1): runs on chosen dates, each at its own time, local to the schedule's zone
 * (`date` `YYYY-MM-DD`, `time` `05:00PM`). The editor's shape of the body `datesScheduleSchema`
 * checks (round 10 wave 2, S3); the server stores the instants and the runner fires them.
 */
export type FriendlyDatesBody = {
  frequency: "Dates";
  runDates: Array<{ date: string; time: string }>;
};

/**
 * The zod shape and the `FriendlySchedule` the cron utilities take are one contract in two
 * places; this makes a drift between them a compile error rather than a runtime surprise.
 */
const _friendlyShapesAgree = (value: FriendlyScheduleBody): FriendlySchedule => value;

/**
 * Round 10 wave 2 (S3): what a create or an update may send as `friendly`: the cron builder's
 * shapes, or Once's `Dates`. `friendlyScheduleSchema` stays the cron builder alone (the MCP tools
 * and `friendlyToCron` take it); a `Dates` body has no expression and is stored as `run_dates`.
 */
export const scheduleFriendlyBodySchema = z.discriminatedUnion("frequency", [
  timesScheduleSchema,
  intervalScheduleSchema,
  datesScheduleSchema,
]);
export type ScheduleFriendlyBody = z.infer<typeof scheduleFriendlyBodySchema>;
export type FriendlyDatesScheduleBody = Extract<ScheduleFriendlyBody, { frequency: "Dates" }>;

/**
 * An IANA name ICU knows. Validated here so the user gets a field error about the timezone:
 * cron-parser accepts an unknown zone at parse time and only fails later, from the next
 * occurrence, with a message about a timestamp.
 */
export const timezoneSchema = z
  .string()
  .min(1)
  .max(MAX_TIMEZONE_LENGTH)
  .refine(isValidTimezone, "Choose a timezone such as America/Los_Angeles.");

/**
 * The United States time zones this product offers (FR-50); the schedule editor offers UTC after
 * them (`SCHEDULE_TIMEZONE_IDS`, walk of 9a9d586, D-e).
 *
 * skyData is sold in the United States, so a dropdown of the four hundred zones ICU knows is
 * four hundred ways to mis-schedule a clinical import. The list is the seven US zones that differ
 * from each other in law, east to west: the four continental ones, Arizona (which does not
 * observe daylight saving), Alaska and Hawaii (Session 10, B6: seven, not ten).
 *
 * Michigan, Indiana and southern Idaho were offered as their own entries until Session 10. They
 * keep the same clock as Eastern and Mountain today, so offering them made the list longer
 * without offering a different time; they are `LEGACY_TIMEZONES` now: still accepted on every
 * write, shown (with a label, never the raw IANA id) only when a row already holds one.
 *
 * `label` is what a person calls the zone and `abbreviation` is the generic form. Neither says
 * "no daylight saving" about Arizona or Hawaii: the abbreviation shown reads MST and HST in July
 * as well as in January, which is the same fact without a parenthesis inside a parenthesis. The
 * abbreviation actually SHOWN is asked of `Intl` at render time, so a screen says PDT in
 * August and PST in January; this one is the fallback for a runtime whose ICU answers with a
 * GMT offset instead of a name.
 */
export interface UsTimezone {
  /** The IANA name, which is what the column stores. */
  zone: string;
  label: string;
  abbreviation: string;
}

export const US_TIMEZONES: readonly UsTimezone[] = [
  { zone: "America/New_York", label: "Eastern", abbreviation: "ET" },
  { zone: "America/Chicago", label: "Central", abbreviation: "CT" },
  { zone: "America/Denver", label: "Mountain", abbreviation: "MT" },
  { zone: "America/Phoenix", label: "Arizona", abbreviation: "MST" },
  { zone: "America/Los_Angeles", label: "Pacific", abbreviation: "PT" },
  { zone: "America/Anchorage", label: "Alaska", abbreviation: "AKT" },
  { zone: "Pacific/Honolulu", label: "Hawaii", abbreviation: "HST" },
] as const;

/** The seven US zones, in the order a picker offers them (the display zone's "United States"). */
export const US_TIMEZONE_IDS: readonly string[] = US_TIMEZONES.map((entry) => entry.zone);

/**
 * The zones the schedule editor offers: the seven, then `UTC` (walk of 9a9d586, 2026-10-02, D-e:
 * a person whose display zone is UTC saw every new schedule start in Eastern). The runner
 * evaluates a schedule's cron in its stored zone (`cron-parser` `tz`), and UTC has been written
 * and run since the first seed, so offering it changes no write rule: it was always accepted.
 */
export const SCHEDULE_TIMEZONE_IDS: readonly string[] = [...US_TIMEZONE_IDS, "UTC"];

/**
 * Zones that are never offered and are still accepted on every write.
 *
 * Michigan, Indiana and southern Idaho: offered until Session 10, so rows (and API callers)
 * hold them. A create naming one is still a 201; the picker shows one only when the row
 * already holds it. (`UTC` was here until the walk of 9a9d586: it is offered now.)
 */
export const LEGACY_TIMEZONES: readonly string[] = [
  "America/Detroit",
  "America/Indiana/Indianapolis",
  "America/Boise",
];

/** True when the schedule editor may offer this zone (`SCHEDULE_TIMEZONE_IDS`). */
export function isSelectableTimezone(zone: string): boolean {
  return SCHEDULE_TIMEZONE_IDS.includes(zone);
}

/** True when a write may set this zone on any schedule. */
export function isAcceptedTimezone(zone: string): boolean {
  return isSelectableTimezone(zone) || LEGACY_TIMEZONES.includes(zone);
}

export const TIMEZONE_NOT_US_ISSUE =
  "Choose a United States time zone or UTC, such as America/New_York or America/Los_Angeles.";

/** The zone a write may set, as opposed to the zone a row may already hold. */
export const usTimezoneSchema = timezoneSchema.refine(isAcceptedTimezone, TIMEZONE_NOT_US_ISSUE);

/**
 * How a schedule's runs import (Session 10, P9, decision B3). `connection`: each connection's own
 * refresh type. `full`: every run this schedule starts is a full refresh, whatever its
 * connections say.
 *
 * Round 4, item 7: the choice is per connection (`fullRefreshDataflowIds`), and `refreshMode` is
 * kept for one release. Sent alone it still means what it meant (`full`: every connection,
 * `connection`: none); the server derives it (`full` when every incremental connection of the
 * schedule is in the set) and answers it. Round 7 (instance 0018): no column stores it; it is
 * derived on every read.
 */
export const SCHEDULE_REFRESH_MODES = ["connection", "full"] as const;
export type ScheduleRefreshMode = (typeof SCHEDULE_REFRESH_MODES)[number];

/** At most this many skip dates on one schedule, before past ones are pruned. */
export const MAX_SKIP_DATES = 100;

/** A pause ends at most this many days ahead. */
export const MAX_PAUSE_DAYS = 366;

/**
 * Local calendar days in the schedule's zone on which it does not fire. The server removes
 * duplicates and days already past in that zone on every write.
 */
export const skipDatesSchema = z
  .array(z.string().refine(isRealDate, "Use a real date such as 2026-12-25."))
  .max(MAX_SKIP_DATES, `A schedule can skip at most ${MAX_SKIP_DATES} dates.`);

const scheduleBodyShape = {
  name: z.string().trim().min(1).max(200),
  /** Exactly one of `cron` and `friendly`; see the refinement below. */
  cron: z.string().trim().min(1).max(MAX_CRON_LENGTH).optional(),
  friendly: scheduleFriendlyBodySchema.optional(),
  /**
   * Any zone ICU knows, at this layer. A create narrows it to the US list below; an update
   * cannot, because the zone a row already holds has to stay writable and this schema has
   * never seen the row — `ScheduleService.update` does that half.
   *
   * The default stays `UTC` rather than becoming a US zone: it is what every API caller that
   * omits the field has been getting, and moving it would quietly re-time their schedules.
   */
  timezone: timezoneSchema.default("UTC"),
  enabled: z.boolean().default(true),
  /**
   * Ordered; every id must name a `ready` dataflow or the write is a 422. May be empty when
   * `dbtJobIds` is not (round 6, S1); never both.
   */
  dataflowIds: z.array(z.uuid()).max(200).default([]),
  dbtJobIds: z.array(z.uuid()).max(200).default([]),
  /**
   * Session 10 (P9). Both optional on create (none, `connection`) and on update, where an omitted
   * field keeps what the row holds: an API caller that predates them must not clear them by
   * renaming a schedule.
   */
  skipDates: skipDatesSchema.optional(),
  refreshMode: z.enum(SCHEDULE_REFRESH_MODES).optional(),
  /**
   * Round 4, item 7: the connections of this schedule that import every row again on each
   * scheduled run; the others use their own refresh type. Each must be one of `dataflowIds`.
   * When sent it decides, and a `refreshMode` beside it is ignored; left out on an update it keeps
   * the stored set (less a connection the schedule no longer holds), unless `refreshMode` is sent.
   */
  fullRefreshDataflowIds: z.array(z.uuid()).max(200).optional(),
  /**
   * Round 7 (backlog "Check freshness before a transform-only schedule runs", instance 0019): a
   * scheduled firing runs `dbt source freshness` over the sources its dbt jobs read first, and
   * skips the build when one is past its freshness limit. Off on a create that leaves it out; an
   * update that leaves it out keeps what the row holds. No default here (PUT keep-when-absent).
   */
  checkSourceFreshness: z.boolean().optional(),
};

/**
 * Round 6, S1: a schedule runs connections, transformations, or both, never neither. With no
 * connection its models run over what is already in the warehouse (an external system may write
 * the tables they read). Reported on `dataflowIds`, which the editor maps onto What runs.
 */
export const NOTHING_TO_RUN_ISSUE = "Choose at least one connection or dbt job.";

function requireSomethingToRun(
  value: { dataflowIds: readonly string[]; dbtJobIds: readonly string[] },
  ctx: z.RefinementCtx,
): void {
  if (value.dataflowIds.length > 0 || value.dbtJobIds.length > 0) return;
  ctx.addIssue({ code: "custom", path: ["dataflowIds"], message: NOTHING_TO_RUN_ISSUE });
}

/** Round 4, item 7: the forced set names only connections the schedule imports. */
function requireFullRefreshSubset(
  value: { dataflowIds: readonly string[]; fullRefreshDataflowIds?: readonly string[] },
  ctx: z.RefinementCtx,
): void {
  const members = new Set(value.dataflowIds);
  if ((value.fullRefreshDataflowIds ?? []).some((id) => !members.has(id))) {
    ctx.addIssue({
      code: "custom",
      path: ["fullRefreshDataflowIds"],
      message: "Name only connections this schedule imports.",
    });
  }
}

function requireExactlyOneSource(
  value: { cron?: string; friendly?: unknown },
  ctx: z.RefinementCtx,
): void {
  const hasCron = Boolean(value.cron);
  const hasFriendly = Boolean(value.friendly);
  if (hasCron === hasFriendly) {
    ctx.addIssue({
      code: "custom",
      path: ["cron"],
      message: hasCron
        ? "Send either a cron expression or the friendly builder, not both."
        : "A schedule needs a cron expression or the friendly builder.",
    });
  }
}

/**
 * The first place a body's times leave the quarter hour (round 5), as the field a refusal names:
 * the builder's offending time (`friendly.cronSchedules.{i}`), or `cron` for a raw expression.
 * Null when every time is on the quarter hour, and for anything another rule refuses (a time that
 * does not parse, an expression that does not): that rule says what is wrong. An interval is on
 * the quarter hour by construction (`*\/15`, `*\/30`, `0`).
 */
export function offQuarterHourField(value: {
  cron?: string;
  friendly?: ScheduleFriendlyBody;
}): string | null {
  const friendly = value.friendly;
  // Once's times are checked on the quarter hour by its own schema (`datesScheduleSchema`).
  if (friendly && friendly.frequency !== "Interval" && friendly.frequency !== "Dates") {
    const index = friendly.cronSchedules.findIndex((slot) => {
      try {
        return !isQuarterHourMinute(parseTime12h(slot).minute);
      } catch {
        return false;
      }
    });
    return index === -1 ? null : `friendly.cronSchedules.${index}`;
  }
  if (value.cron && offQuarterMinutes(value.cron).length > 0) return "cron";
  return null;
}

/** A NEW schedule's times are on the quarter hour (round 5); an update is the service's call. */
function requireQuarterHours(
  value: { cron?: string; friendly?: ScheduleFriendlyBody },
  ctx: z.RefinementCtx,
): void {
  const field = offQuarterHourField(value);
  if (!field) return;
  ctx.addIssue({
    code: "custom",
    path: field.split(".").map((part) => (/^\d+$/.test(part) ? Number(part) : part)),
    message: QUARTER_HOUR_ISSUE,
  });
}

/** A new schedule has no stored zone to respect, so the US list is the whole answer here. */
export const createScheduleBodySchema = z
  .object({ ...scheduleBodyShape, timezone: usTimezoneSchema.default("UTC") })
  .superRefine(requireExactlyOneSource)
  .superRefine(requireSomethingToRun)
  .superRefine(requireFullRefreshSubset)
  .superRefine(requireQuarterHours);
export type CreateScheduleBody = z.infer<typeof createScheduleBodySchema>;

/**
 * PUT replaces the schedule, so it carries the same fields a create does — with one difference:
 * the zone is only checked for being a real zone here, and `ScheduleService.update` then allows
 * the US list PLUS whatever the row already holds. A schedule stored in a zone this product no
 * longer offers must not become unsaveable because somebody renamed it.
 */
export const updateScheduleBodySchema = z
  .object(scheduleBodyShape)
  .superRefine(requireExactlyOneSource)
  .superRefine(requireSomethingToRun)
  .superRefine(requireFullRefreshSubset);
export type UpdateScheduleBody = z.infer<typeof updateScheduleBodySchema>;

/**
 * `POST /schedules/:id/pause` (Session 10, P9). `until` is an instant: nothing fires before it,
 * and an occurrence exactly at it fires. After now and at most `MAX_PAUSE_DAYS` ahead; the server
 * checks both, because "now" is its clock.
 */
export const pauseScheduleBodySchema = z.object({
  until: z.iso.datetime({ offset: true }),
});
export type PauseScheduleBody = z.infer<typeof pauseScheduleBodySchema>;

export const schedulesQuerySchema = z.object({
  enabled: z
    .enum(["true", "false"])
    .optional()
    .transform((value) => (value === undefined ? undefined : value === "true")),
});
export type SchedulesQuery = z.infer<typeof schedulesQuerySchema>;

/**
 * Why a due firing made no run (the `reason` of a `schedule.skip` audit row, written by the due
 * scan): a skip date, a pause, or a previous run still holding the schedule or one of its
 * connections (one run per connection at a time).
 *
 * `connection_disabled` (2026-09-28) is the one reason that can sit beside a run: the firing left
 * out the connections that were disabled (`dataflowIds`) and imported the others. It made no run
 * only when every connection of the schedule was disabled. `connection_untested` (round 8) is the
 * same for a connection whose target changed since its last connection test, and
 * `connection_not_ready` (#1471) for any other Draft connection (never validated, a failed test,
 * a credential missing), once per episode. `connection_busy`
 * (round 9, B-28) is a firing that met a run of ANOTHER schedule (or one started by hand) holding
 * its connection: the row names the holder (`holderScheduleName`, `runId`); `previous_run_running`
 * is now only the schedule's own open run. `connection_deleted` (round 9, B-01) names connections
 * the schedule still listed but that no longer exist; the id is dropped and the rest fire.
 * `destination_not_set` (#375) and `destination_not_ready` (#923) are a firing skipped whole
 * because the instance's destination is not set or has not passed its test; one row per schedule
 * per episode (`runner/scheduler/dueScan.ts` `skipForDestination`); `destination_coming_soon` is
 * a Databricks destination (owner 2026-10-13, 2B).
 */
export const SCHEDULE_SKIP_REASONS = [
  "skip_date",
  "paused",
  "previous_run_running",
  "connection_disabled",
  "connection_untested",
  "connection_not_ready",
  "connection_busy",
  "connection_deleted",
  "destination_not_set",
  "destination_not_ready",
  "destination_coming_soon",
] as const;
export type ScheduleSkipReason = (typeof SCHEDULE_SKIP_REASONS)[number];

/** `GET /schedules/:id/skips?limit=`: newest first, 50 by default, at most 100. */
export const scheduleSkipsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

/** One firing a schedule skipped. Identifiers, instants and the reason only. */
export interface ScheduleSkip {
  /** When the runner recorded the skip. */
  at: string;
  /** The firing that was skipped; null on rows written before it was recorded. */
  scheduledFor: string | null;
  /** The firing's local date in the schedule's zone (`YYYY-MM-DD`). */
  date: string;
  reason: ScheduleSkipReason;
  /** With `previous_run_running`: the run that still held the work (open it to see why). */
  runId: string | null;
  /**
   * Only with `connection_disabled` (2026-09-28), `connection_untested` (round 8) and
   * `connection_not_ready` (#1471): the
   * connections the firing left out because they were disabled, or because their target changed
   * since their last connection test, or (round 9) `connection_deleted`/`connection_busy` for the
   * connections named. Ids only; the field is absent on every other reason.
   */
  dataflowIds?: string[];
  /** Only with `connection_busy` (round 9, B-28): the schedule whose run held the connection. */
  holderScheduleId?: string | null;
  holderScheduleName?: string | null;
  /**
   * Round 10 wave 2 (A1): only with `connection_busy` on a firing that still ran its free
   * connections: the run it made. Absent on a whole-firing skip. #1471: also with
   * `connection_not_ready` when the firing ran its other connections (no `holders` then).
   */
  firedRunId?: string;
  /** With `firedRunId`: how many connections that run imports. */
  importedCount?: number;
  /** With `firedRunId`: each busy connection and the run (and schedule) that held it. */
  holders?: Array<{
    dataflowId: string;
    runId: string;
    holderScheduleId: string | null;
    holderScheduleName: string | null;
  }>;
}

export const scheduleSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  cron: z.string().nullable(),
  /** The FR-50 friendly rendering of `cron`; decoration, never parsed back. */
  description: z.string().nullable(),
  timezone: z.string(),
  enabled: z.boolean(),
  kind: z.enum(SCHEDULE_KINDS),
  dataflowIds: z.array(z.uuid()),
  dbtJobIds: z.array(z.uuid()),
  /**
   * The next firing, after skip dates and any pause. Null for a disabled schedule, and for an
   * enabled one with no allowed firing inside the next two years: "No upcoming run". Such a
   * schedule is never disabled for it; the next write recomputes it.
   */
  nextFireAt: z.string().nullable(),
  /** Local dates (`YYYY-MM-DD`, in `timezone`) on which it does not fire; ascending. */
  skipDates: z.array(z.string()),
  /** No firing before this instant; null when not paused. May be in the past (an ended pause). */
  pausedUntil: z.string().nullable(),
  refreshMode: z.enum(SCHEDULE_REFRESH_MODES),
  /**
   * Round 4, item 7: the connections that import in full on every scheduled run, in the
   * schedule's order. Every server since that release sends it; absent from an older one, where
   * `refreshMode: "full"` means every connection.
   */
  fullRefreshDataflowIds: z.array(z.uuid()).optional(),
  /** Round 7: whether a scheduled firing checks its dbt sources' freshness first. Absent from an older server. */
  checkSourceFreshness: z.boolean().optional(),
  lastFiredAt: z.string().nullable(),
  /**
   * #1006: the newest run carrying this schedule's id, a scheduled firing or a Run now of the
   * whole schedule (the same runs Success counts). Falls back to `schedules.last_run_id`.
   */
  lastRunId: z.string().nullable(),
  /** #1006: when that run started, else was created. Absent from an older server and on a write answer. */
  lastRunAt: z.string().nullable().optional(),
  /** #1006: that run was started by a person (Run now), not by the scheduler. */
  lastRunManual: z.boolean().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * Round 10 wave 2 (S3 "Once"): the instants a dates schedule fires at, ascending (UTC); empty
   * for a cron schedule, whose `cron` is then set. Absent from an older server.
   */
  runDates: z.array(z.string()).optional(),
  /**
   * Round 10 wave 2 (S5): enabled, with dates, every one of them past and nothing left to fire
   * (`nextFireAt` null): the list's "Completed". A pause past the remaining dates is not this
   * (`isOnceCompleted`, `server/services/ScheduleService.ts`). Absent from an older server.
   */
  completed: z.boolean().optional(),
});
export type ScheduleSummary = z.infer<typeof scheduleSummarySchema>;
